'use client';

import { useEffect, useState } from 'react';

import { consentStorageAdapter } from '../adapters';
import { getConsentStatusFactory } from '../domain/get-consent-status';
import { ConsentStatus } from '../domain/types';
import type { ConsentChoice } from '../domain/types';
import { useConsentContext } from './consent-context-provider';

/**
 * Return type for the useConsent hook
 */
export interface UseConsentReturn {
  /**
   * Current consent status (GPC-aware)
   */
  status: ConsentStatus;
  /**
   * Whether the consent status is still being loaded
   */
  isLoading: boolean;
  /**
   * Function to save user's consent choice
   */
  saveConsent: (choice: ConsentChoice) => void;
  /**
   * Function to clear saved consent
   */
  clearConsent: () => void;
}

/**
 * React hook for accessing and managing user consent state
 *
 * Provides reactive access to the user's consent status and methods
 * to update or clear their consent choice. This hook is GPC-aware and will
 * automatically return REQUIRED_COOKIES status when GPC is enabled in
 * strict consent states (e.g., California).
 *
 * @returns Consent state and methods
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { status, saveConsent } = useConsent();
 *
 *   if (status === ConsentStatus.UNKNOWN) {
 *     return <ConsentBanner />;
 *   }
 *
 *   if (status === ConsentStatus.ALL_COOKIES) {
 *     return <AnalyticsScripts />;
 *   }
 *
 *   return null;
 * }
 * ```
 */
export function useConsent(): UseConsentReturn {
  const { requiresConsent } = useConsentContext();
  const [status, setStatus] = useState<ConsentStatus>(ConsentStatus.UNKNOWN);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadConsentStatus = async () => {
      try {
        // Get initial consent status from storage (GPC-aware)
        const getConsentStatus = getConsentStatusFactory({
          storage: consentStorageAdapter,
          requiresConsent,
        });

        const result = await getConsentStatus();

        if (result.success) {
          setStatus(result.data);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadConsentStatus();
  }, [requiresConsent]);

  const saveConsent = (choice: ConsentChoice) => {
    consentStorageAdapter.saveConsent(choice);
    setStatus(
      choice === 'all_cookies'
        ? ConsentStatus.ALL_COOKIES
        : ConsentStatus.REQUIRED_COOKIES,
    );
  };

  const clearConsent = () => {
    consentStorageAdapter.clearConsent();
    setStatus(ConsentStatus.UNKNOWN);
  };

  return {
    status,
    isLoading,
    saveConsent,
    clearConsent,
  };
}
